/**
 * Hata kayıtları — "hangi fatura kesilemedi, neden?"
 *
 * Bu ekranın tek işi var: bir aksaklık olduğunda sunucuya bağlanmadan sebebi
 * bulmak. O yüzden tasarım kararları hep aynı soruya bakar — **buraya bakan
 * kişi telaşlı ve teknik değil.**
 *
 * 1. **Varsayılan görünüm gruplu.** Aynı arıza 340 kez tekrarladıysa 340 satır
 *    değil "1 arıza · 340 kez" görünür. Tek tek görmek isteyene anahtar var.
 *
 * 2. **Varsayılan filtre "açık".** Kapatılmış kayıtlar listeyi doldurmasın;
 *    ekranı açan kişi şu an ne bozuk onu arıyor.
 *
 * 3. **Ham hata cinsi tek başına gösterilmez.** `ECONNREFUSED` yerine
 *    "Bağlantı reddedildi — servis kapalı olabilir"; ham kod ikinci satırda
 *    küçük punto ile durur (CLAUDE.md kural 6 — teknik jargon gizlenmez ama
 *    öne çıkmaz).
 *
 * 4. **Fatura kodu bağlantıdır.** Hatadan faturaya tek tıkla gidilir; asıl
 *    soru zaten "hangi sipariş etkilendi".
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useAdresParametreleri } from '../lib/adres';
import type { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2, RotateCw, Layers, List } from 'lucide-react';
import { api, type HataOzeti, type DurumTonu } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Girdi, Secim } from '../components/ui/Girdi';
import { DurumRozeti } from '../components/DurumRozeti';
import { HataKutusu } from '../components/Durumlar';
import { VeriTablosu } from '../components/VeriTablosu';
import { HataDetayPenceresi } from '../components/HataDetayPenceresi';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';
import { TarihHucresi } from '../components/TarihHucresi';
import { sayfaBilgisi } from '../lib/sayfa';

const ALANLAR = [
  { deger: '', etiket: 'Tüm bölümler' },
  { deger: 'fatura', etiket: 'Fatura kesme' },
  { deger: 'kuyruk', etiket: 'Arka plan işi' },
  { deger: 'veriban', etiket: 'Veriban servisi' },
  { deger: 'shopify', etiket: 'Shopify bildirimi' },
  { deger: 'bildirim', etiket: 'Mail / SMS' },
  { deger: 'panel', etiket: 'Panel' },
  { deger: 'sistem', etiket: 'Sistem' },
];

const SEVIYELER = [
  { deger: '', etiket: 'Tüm seviyeler' },
  { deger: 'CRITICAL', etiket: 'Kritik — müdahale gerekiyor' },
  { deger: 'ERROR', etiket: 'Hata — tekrar denenecek' },
  { deger: 'WARNING', etiket: 'Uyarı' },
];

const DURUMLAR = [
  { deger: 'acik', etiket: 'Açık olanlar' },
  { deger: 'cozuldu', etiket: 'Kapatılmışlar' },
  { deger: '', etiket: 'Hepsi' },
];

/**
 * Seviye → rozet tonu.
 *
 * Tek eşleme tablosu (CLAUDE.md kural 2): hiçbir yer kendi rengini uydurmaz.
 */
function seviyeTonu(seviye: HataOzeti['seviye']): DurumTonu {
  if (seviye === 'CRITICAL') return 'danger';
  if (seviye === 'ERROR') return 'warning';
  return 'info';
}

export function HataKayitlari() {
  const { parametreler, adresiGuncelle } = useAdresParametreleri();
  const { kullanici } = useOturum();
  const kuyruk = useQueryClient();
  const [acikKayit, setAcikKayit] = useState<string | null>(null);

  const alan = parametreler.get('alan') ?? '';
  const seviye = parametreler.get('seviye') ?? '';
  // Varsayılan "açık": ekranı açan kişi şu an neyin bozuk olduğunu arıyor.
  const durum = parametreler.get('durum') ?? 'acik';
  const q = parametreler.get('q') ?? '';
  const grupla = parametreler.get('grupla') ?? '1';
  const sayfaNo = Math.max(1, Number(parametreler.get('page') ?? '1') || 1);
  const sayfaBoyu = Number(parametreler.get('pageSize') ?? '25') || 25;

  const [aramaKutusu, setAramaKutusu] = useState(q);

  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['hatalar', { alan, seviye, durum, q, grupla, sayfaNo, sayfaBoyu }],
    queryFn: () =>
      api.hataKayitlari({
        page: sayfaNo,
        pageSize: sayfaBoyu,
        grupla,
        ...(alan ? { alan } : {}),
        ...(seviye ? { seviye } : {}),
        ...(durum ? { durum } : {}),
        ...(q ? { q } : {}),
      }),
    placeholderData: (onceki) => onceki,
    // Hata ekranı canlı olmalı: açık bırakan kişi yeni arızayı görmeli.
    refetchInterval: 30_000,
  });

  const cozumleme = useMutation({
    mutationFn: (girdi: { id: string; not?: string; tumGrup: boolean }) =>
      api.hataCozuldu(girdi.id, {
        tumGrup: girdi.tumGrup,
        ...(girdi.not ? { not: girdi.not } : {}),
      }),
    onSuccess: () => {
      void kuyruk.invalidateQueries({ queryKey: ['hatalar'] });
      void kuyruk.invalidateQueries({ queryKey: ['hata-detay'] });
    },
  });

  const kolonlar = useMemo<ColumnDef<HataOzeti, unknown>[]>(
    () => [
      {
        id: 'tarih',
        header: 'Ne zaman',
        enableSorting: false,
        cell: ({ row }) => <TarihHucresi zaman={row.original.tarih} />,
      },
      {
        id: 'seviye',
        header: 'Seviye',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <DurumRozeti ton={seviyeTonu(row.original.seviye)} etiket={row.original.seviyeEtiketi} />
            {row.original.tekrar > 1 && (
              <span className="rakam text-xs text-[var(--ink-faint)]">{row.original.tekrar} kez</span>
            )}
          </div>
        ),
      },
      {
        id: 'ne',
        header: 'Ne oldu',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-0 max-w-xl flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-[var(--ink)]">{row.original.cinsEtiketi}</span>
            <span className="line-clamp-2 text-xs text-[var(--ink-muted)]">{row.original.mesaj}</span>
            <span className="rakam text-xs text-[var(--ink-faint)]">
              {row.original.alanEtiketi} · {row.original.islem}
              {row.original.httpKodu !== null && ` · HTTP ${row.original.httpKodu}`}
            </span>
          </div>
        ),
      },
      {
        id: 'fatura',
        header: 'İlgili fatura',
        enableSorting: false,
        cell: ({ row }) => {
          const kod = row.original.faturaKodu;
          if (kod === null) {
            return <span className="text-xs text-[var(--ink-faint)]">Faturaya bağlı değil</span>;
          }
          return (
            <div className="flex min-w-0 flex-col">
              <Link
                to={`/faturalar/${encodeURIComponent(kod)}`}
                className="rakam truncate text-sm text-[var(--accent)] hover:underline"
              >
                {row.original.faturaNumarasi ?? kod}
              </Link>
              {row.original.faturaNumarasi !== null && (
                <span className="rakam truncate text-xs text-[var(--ink-faint)]">{kod}</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'durum',
        header: 'Durum',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.cozuldu ? (
            <div className="flex flex-col whitespace-nowrap">
              <span className="inline-flex items-center gap-1 text-xs text-[var(--state-ok)]">
                <CheckCircle2 size={13} aria-hidden="true" />
                Kapatıldı
              </span>
              <span className="truncate text-xs text-[var(--ink-faint)]">{row.original.cozen ?? '—'}</span>
            </div>
          ) : (
            <span className="text-xs text-[var(--ink-muted)]">
              {row.original.tekrarDenenecek ? 'Tekrar denenecek' : 'Denenmeyecek'}
            </span>
          ),
      },
    ],
    []
  );

  const acikSayisi = data?.items.filter((h) => !h.cozuldu).length ?? 0;

  return (
    <Kabuk
      baslik="Hata kayıtları"
      kirintilar={[{ etiket: 'Denetim' }, { etiket: 'Hata kayıtları' }]}
      altBaslik={
        data
          ? `${data.total} ${data.gruplu ? 'arıza' : 'kayıt'}${acikSayisi > 0 ? ` · ${acikSayisi} tanesi bu sayfada açık` : ''}`
          : 'Arka planda ne patladı, hangi fatura etkilendi'
      }
      eylemler={
        <>
          <Dugme
            gorunum="ikincil"
            ikon={grupla === '1' ? <List size={15} /> : <Layers size={15} />}
            onClick={() => adresiGuncelle({ grupla: grupla === '1' ? '0' : '1' })}
          >
            {grupla === '1' ? 'Tek tek göster' : 'Aynıları grupla'}
          </Dugme>
          <Dugme
            gorunum="ikincil"
            ikon={<RotateCw size={15} />}
            bekliyor={isFetching && !isPending}
            onClick={() => void refetch()}
          >
            Yenile
          </Dugme>
        </>
      }
    >
      <div className="kap uzuv flex flex-col gap-4 p-6">
        <form
          className="flex flex-col gap-3 @2xl:flex-row @2xl:items-end"
          onSubmit={(olay) => {
            olay.preventDefault();
            adresiGuncelle({ q: aramaKutusu.trim() });
          }}
        >
          <div className="min-w-0 flex-1">
            <Girdi
              etiket="Ara"
              type="search"
              value={aramaKutusu}
              onChange={(olay) => setAramaKutusu(olay.target.value)}
              placeholder="Fatura kodu, hata mesajı veya işlem adı"
            />
          </div>
          <Dugme type="submit" gorunum="birincil">
            Ara
          </Dugme>
        </form>

        <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
          <Secim
            etiket="Durum"
            value={durum}
            onChange={(olay) => adresiGuncelle({ durum: olay.target.value })}
          >
            {DURUMLAR.map((s) => (
              <option key={s.deger} value={s.deger}>
                {s.etiket}
              </option>
            ))}
          </Secim>
          <Secim
            etiket="Seviye"
            value={seviye}
            onChange={(olay) => adresiGuncelle({ seviye: olay.target.value })}
          >
            {SEVIYELER.map((s) => (
              <option key={s.deger} value={s.deger}>
                {s.etiket}
              </option>
            ))}
          </Secim>
          <Secim etiket="Bölüm" value={alan} onChange={(olay) => adresiGuncelle({ alan: olay.target.value })}>
            {ALANLAR.map((s) => (
              <option key={s.deger} value={s.deger}>
                {s.etiket}
              </option>
            ))}
          </Secim>
        </div>
      </div>

      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      <Kart>
        <VeriTablosu
          veri={data?.items ?? []}
          kolonlar={kolonlar}
          sayfa={sayfaBilgisi(data, sayfaNo, sayfaBoyu)}
          siralama={[]}
          yukleniyor={isPending}
          tazeleniyor={isFetching && !isPending}
          satirAnahtari={(satir) => satir.id}
          onSiralama={() => undefined}
          onSayfa={(yeni) => adresiGuncelle({ page: yeni > 1 ? yeni : undefined }, false)}
          onSayfaBoyu={(boy) => adresiGuncelle({ pageSize: boy })}
          onSatirTikla={(satir) => setAcikKayit(satir.id)}
          bosBaslik={durum === 'acik' ? 'Açık hata yok — her şey yolunda' : 'Bu filtrede kayıt yok'}
          bosAciklama={
            durum === 'acik'
              ? 'Arka planda başarısız olan bir işlem bulunmuyor.'
              : 'Filtreyi genişletmeyi deneyin.'
          }
        />
      </Kart>

      {acikKayit !== null && (
        <HataDetayPenceresi
          id={acikKayit}
          onKapat={() => setAcikKayit(null)}
          cozebilir={izinVar(kullanici, 'hata.duzenle')}
          cozumleniyor={cozumleme.isPending}
          onCoz={(girdi) => {
            cozumleme.mutate(
              { id: acikKayit, tumGrup: girdi.tumGrup, ...(girdi.not ? { not: girdi.not } : {}) },
              { onSuccess: () => setAcikKayit(null) }
            );
          }}
        />
      )}
    </Kabuk>
  );
}
